import { useCallback, useRef, useState } from "react";
import type { LatLng } from "../lib/geoUtils";

/**
 * 現在地取得の状態。検索モードと並列な「nearby mode」のステートマシン。
 *
 *   off     : 機能未使用 (デフォルト)
 *   loading : getCurrentPosition 発行済、レスポンス待ち
 *   active  : 現在地取得成功、location が有効
 *   error   : 権限拒否 / タイムアウト / 非対応ブラウザ等で失敗
 */
export type NearbyState =
  | { status: "off" }
  | { status: "loading" }
  | { status: "active"; location: LatLng; timestamp: number }
  | { status: "error"; code: NearbyErrorCode; message: string };

export type NearbyErrorCode =
  | "unsupported" // navigator.geolocation が無い
  | "denied" // ユーザーが権限を拒否した
  | "unavailable" // 位置情報サービス OFF 等
  | "timeout"
  | "unknown";

export interface UseGeolocationResult {
  state: NearbyState;
  /** 現在地取得を開始する。既に loading / active でも強制的に再取得する。 */
  requestLocation: () => void;
  /** nearby mode を解除して off に戻す。 */
  clear: () => void;
}

/**
 * HTML5 Geolocation API の薄いラッパ。
 *
 * - `navigator.geolocation.getCurrentPosition` を使う (1 回限りの取得。
 *   `watchPosition` ではない = 継続課金/バッテリ消費を避ける)
 * - HTTPS または localhost 以外では必ず `unavailable` 相当になる仕様なので
 *   呼び出し側でエラーハンドリングしやすい形に正規化して返す
 * - timeout 10 秒、`enableHighAccuracy: true` で GPS 優先 (室内でも WiFi fallback 有)
 * - AbortController 相当は無いので stale callback ガードのため requestId を使う
 */
export function useGeolocation(): UseGeolocationResult {
  const [state, setState] = useState<NearbyState>({ status: "off" });
  // 同時並行リクエストの最新 ID を保持。古いリクエストのコールバックが
  // 後から発火しても state を上書きしないようにする。
  const latestRequestIdRef = useRef(0);

  const requestLocation = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState({
        status: "error",
        code: "unsupported",
        message: "お使いのブラウザは位置情報に対応していません",
      });
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    setState({ status: "loading" });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // 古いリクエストの結果は無視する。
        if (requestId !== latestRequestIdRef.current) return;
        setState({
          status: "active",
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
          timestamp: position.timestamp,
        });
      },
      (err) => {
        if (requestId !== latestRequestIdRef.current) return;
        const code = normalizeErrorCode(err);
        setState({
          status: "error",
          code,
          message: errorMessageFor(code),
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10_000,
        // 5 分以内に取得済みの位置があれば再取得せず使う (バッテリ節約)。
        maximumAge: 5 * 60 * 1000,
      },
    );
  }, []);

  const clear = useCallback(() => {
    // 進行中のリクエストを無効化 (stale callback 対策)。
    latestRequestIdRef.current++;
    setState({ status: "off" });
  }, []);

  return { state, requestLocation, clear };
}

function normalizeErrorCode(err: GeolocationPositionError): NearbyErrorCode {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return "denied";
    case err.POSITION_UNAVAILABLE:
      return "unavailable";
    case err.TIMEOUT:
      return "timeout";
    default:
      return "unknown";
  }
}

function errorMessageFor(code: NearbyErrorCode): string {
  switch (code) {
    case "unsupported":
      return "お使いのブラウザは位置情報に対応していません";
    case "denied":
      return "位置情報の利用が許可されていません";
    case "unavailable":
      return "位置情報を取得できません (サービス OFF または HTTPS 非対応)";
    case "timeout":
      return "位置情報の取得がタイムアウトしました";
    default:
      return "位置情報の取得に失敗しました";
  }
}
